import { Link } from "react-router-dom";
import { ExternalLink, Hammer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { timeAgo } from "@/lib/api";
import { DeployBadge, SiteBadge } from "./Badges";
import { publicUrl } from "./helpers";

function Pointer({ d, url, serverOnly, testid }) {
  if (!d) return <span className="text-xs text-slate-400">—</span>;
  if (!url || serverOnly) return <span className="text-xs text-slate-600" title={url || "Pubblicata su un altro hosting: ricompila"} data-testid={testid}>n. {d.number}</span>;
  return <a href={publicUrl(url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-sky-700 hover:underline" data-testid={testid}>n. {d.number}<ExternalLink size={11} /></a>;
}

function SiteRow({ s, checked, onCheck, onBuild }) {
  return (
    <tr className="hover:bg-slate-50/60" data-testid={`site-row-${s.id}`}>
      <td className="px-4 py-3"><Checkbox checked={checked} onCheckedChange={(v) => onCheck(s.id, !!v)} aria-label={`Seleziona ${s.name}`} data-testid={`site-select-${s.id}`} /></td>
      <td className="px-3 py-3 max-w-[260px]">
        <Link to={`/sites/${s.id}`} className="block truncate font-medium text-slate-900 hover:text-sky-700" data-testid={`site-link-${s.id}`}>{s.name}</Link>
        <span className="block truncate text-xs text-slate-500 md:hidden">{s.client.name}</span>
        <span className="text-xs text-slate-500">{s.slug}{s.hosting?.edge_enabled && <span className="ml-2 text-sky-700" data-testid={`site-edge-flag-${s.id}`}>· {s.hosting.label}</span>}{s.outdated && <span className="ml-2 text-amber-700">· modifiche da compilare</span>}</span>
      </td>
      <td className="hidden md:table-cell px-3 py-3 text-sm text-slate-700 max-w-[200px] truncate">{s.client.name}</td>
      <td className="px-3 py-3"><SiteBadge status={s.status} testid={`site-status-${s.id}`} /></td>
      <td className="px-3 py-3"><Pointer d={s.preview} url={s.urls.preview} serverOnly={s.hosting?.server_only} testid={`site-preview-${s.id}`} /></td>
      <td className="px-3 py-3"><Pointer d={s.live} url={s.urls.live} serverOnly={s.hosting?.server_only} testid={`site-live-${s.id}`} /></td>
      <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap">
        {s.pending ? <DeployBadge d={{ status: "building", phase_label: "In lavorazione" }} /> : s.last_deploy ? <>n. {s.last_deploy.number} · {timeAgo(s.last_deploy.at)}{s.last_deploy.status === "failed" && <span className="ml-1 text-red-600">(non riuscita)</span>}</> : "Mai compilato"}
      </td>
      <td className="px-4 py-3 text-right">
        <Button size="sm" variant="outline" onClick={() => onBuild(s)} disabled={s.project.deleted} data-testid={`site-build-${s.id}`}><Hammer size={13} className="mr-1.5" />Compila</Button>
      </td>
    </tr>
  );
}

export default function SitesTable({ sites, selected, setSelected, onBuild }) {
  const all = sites.length > 0 && sites.every((s) => selected.includes(s.id));
  const onCheck = (id, v) => setSelected((l) => (v ? [...new Set([...l, id])] : l.filter((x) => x !== id)));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="sites-table">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-4 py-2 text-left"><Checkbox checked={all} onCheckedChange={(v) => setSelected(v ? sites.map((s) => s.id) : [])} aria-label="Seleziona tutti" data-testid="sites-select-all" /></th>
            {["Sito", "Cliente", "Stato", "Anteprima", "Online", "Ultima compilazione"].map((h) => <th key={h} className={`px-3 py-2 text-left font-medium ${h === "Cliente" ? "hidden md:table-cell" : ""}`}>{h}</th>)}
            <th className="px-4 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sites.map((s) => <SiteRow key={s.id} s={s} checked={selected.includes(s.id)} onCheck={onCheck} onBuild={onBuild} />)}
          {!sites.length && <tr><td colSpan={8} className="px-5 py-10 text-center text-sm text-slate-500" data-testid="sites-empty">Nessun sito con questi filtri.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
